// Grant/revoke management roles (owner/maintainer/viewer) on a management-store object
// (`blueprint:<id>`, the Platform's management object id, `client:<id>`). Shared across the Platform, Client and Blueprint
// pages — one mechanism, per issue #478.
//
// ADR-0038 governs the semantics: a subject holds at most one direct role per object. Raising a
// role is a plain grant (a monotonic promotion, reported via `superseded`); lowering one requires
// the `demote` verb with the currently-held role as a concurrency guard. Indirect access (cascaded
// from a group or an ancestor object) is not revocable here — those rows get no action menu.
import {
  faBan,
  faUserCheck,
  faUserGear,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faCrown,
  faTimes,
  faUserPlus,
  faUserShield,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Badge,
  Button,
  FieldGroup,
  Grid,
  Icon,
  Inline,
  Input,
  Label,
  Message,
  Section,
  Tooltip,
} from "@intility/bifrost-react";
import Drawer from "@intility/bifrost-react/Drawer";
import Modal from "@intility/bifrost-react/Modal";
import Select from "@intility/bifrost-react-select";
import type { Columns } from "@intility/gjallarbru";
import { DataTable } from "@intility/gjallarbru";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ReactNode,
  type Ref,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import "./ManagementAccessSection.css";
import { useSearchUsers, useUser } from "~/api/clients";
import { FgaErrorCodes, IntilityIdException } from "~/api/errorEnvelope";
import {
  type AccessPrincipal,
  deleteManagementTuple,
  demoteManagementRole,
  getManagementAccess,
  type ManagementAccessResponse,
  type ManagementSupersededGrant,
  writeManagementTuple,
} from "~/api/management";
import type { UserSearchResult } from "~/api/types";
import { auth } from "~/auth";
import { RoleBadge } from "../RoleBadge";

type ManagementRole = keyof ManagementAccessResponse;

const ROLE_ORDER: ManagementRole[] = ["owner", "maintainer", "viewer"];
const ROLE_RANK: Record<ManagementRole, number> = {
  owner: 3,
  maintainer: 2,
  viewer: 1,
};
const ROLE_LABEL: Record<ManagementRole, string> = {
  owner: "Owner",
  maintainer: "Maintainer",
  viewer: "Viewer",
};

interface AccessRow extends AccessPrincipal {
  relation: ManagementRole;
}

// One row per grant: a subject can have a direct and a stronger inherited row at once.
const accessRowKey = (row: AccessPrincipal) => `${row.subject}|${row.access}`;

// A subject queued in the Manage access drawer. Ones picked from the table keep their
// `principal`, so the name/email lookup still works; ones added in the drawer carry a label.
interface ManageSubject {
  subject: string;
  label: string;
  email: string | null;
  principal?: AccessPrincipal;
}

function subjectDisplay(row: AccessPrincipal): {
  label: string;
  subjectId: string;
} {
  const subjectId = row.subject.slice(row.subject.indexOf(":") + 1);
  return { label: row.info?.displayName ?? subjectId, subjectId };
}

function supersededText(superseded: ManagementSupersededGrant[]): string {
  return superseded
    .map(
      (s) => `${ROLE_LABEL[s.role as ManagementRole] ?? s.role} on ${s.object}`,
    )
    .join(", ");
}

// `GET /api/access` enriches `info` server-side, but that lookup can miss (e.g. a group's
// indirect members, or a race with a just-created user) — fall back to a direct per-user lookup
// against keycloak-api rather than showing a bare GUID.
function useSubjectIdentity(row: AccessPrincipal) {
  const subjectId = row.subject.slice(row.subject.indexOf(":") + 1);
  const needsLookup = row.subjectType === "user" && !row.info?.displayName;
  const { data: user } = useUser(needsLookup ? subjectId : null);

  return {
    displayName: row.info?.displayName ?? user?.displayName ?? null,
    // Mail, or the username (UPN) when the user has no mail address.
    email: row.info?.email ?? user?.email ?? row.info?.username ?? null,
  };
}

function DisplayNameCell({
  row,
  fallbackLabel,
}: {
  row: AccessPrincipal;
  fallbackLabel: string;
}) {
  const { displayName } = useSubjectIdentity(row);
  return (
    displayName ?? (
      <span style={{ fontFamily: "monospace", fontSize: "0.875em" }}>
        {fallbackLabel}
      </span>
    )
  );
}

function EmailCell({
  row,
  className,
}: {
  row: AccessPrincipal;
  className?: string;
}) {
  const { email } = useSubjectIdentity(row);
  return email ? <span className={className}>{email}</span> : null;
}

// A circle: width and height both equal the normal badge height (line height plus the badge's
// vertical padding).
const ICON_BADGE_SIDE = "calc(1lh + 2 * var(--bfs2))";
const ICON_BADGE_STYLE = {
  display: "inline-grid",
  placeItems: "center",
  width: ICON_BADGE_SIDE,
  height: ICON_BADGE_SIDE,
  padding: 0,
} as const;

// The role a queued subject holds today: the role badge, or a neutral "ban" badge when they
// have no access yet.
function CurrentRoleBadge({ role }: { role: ManagementRole | undefined }) {
  if (role) {
    return (
      <RoleBadge
        role={role}
        iconOnly
        tooltip={`Current role: ${ROLE_LABEL[role]}`}
      />
    );
  }
  return (
    <Tooltip content="Current role: None">
      <Badge
        pill
        state="neutral"
        aria-label="Current role: None"
        style={ICON_BADGE_STYLE}
      >
        <Icon icon={faBan} />
      </Badge>
    </Tooltip>
  );
}

function PersonItem({ row }: { row: AccessRow }) {
  return (
    <Inline align="center" gap={12}>
      <Inline.Stretch>
        <span style={{ display: "block" }}>
          <DisplayNameCell
            row={row}
            fallbackLabel={subjectDisplay(row).label}
          />
        </span>
        <EmailCell className="bfc-base-2" row={row} />
      </Inline.Stretch>
      <RoleBadge role={row.relation} iconOnly />
    </Inline>
  );
}

// The people with direct access, listed beside the table.
function PeopleSection({ rows }: { rows: AccessRow[] }) {
  return (
    <Section border radius background="base-2">
      <Section.Header style={{ backgroundColor: "var(--bfc-base-3)" }}>
        <Inline align="center">
          <Icon icon={faCrown} />
          <Inline.Stretch>Granted access</Inline.Stretch>
        </Inline>
      </Section.Header>
      <Section.Content>
        <Grid gap={16}>
          {rows.map((row) => (
            <PersonItem key={row.subject} row={row} />
          ))}
        </Grid>
      </Section.Content>
    </Section>
  );
}

// All grants, selectable for the Manage access drawer. Columns are memoised here (not in the
// section) because the section returns early while loading, which rules out hooks below it.
function DirectAccessTable({
  flush = false,
  rows,
  onGrant,
  showGrantButton = true,
  pinYouAndOwners = false,
  rowSelection,
  onRowSelectionChange,
}: {
  /** No border or radius, for a container that already frames the table. */
  flush?: boolean;
  /** Draw the button for `onGrant`; selection stays on without it. */
  showGrantButton?: boolean;
  /** Put the signed-in user first, then owners, instead of sorting by name. */
  pinYouAndOwners?: boolean;
  rows: AccessRow[];
  rowSelection: Record<string, boolean>;
  onRowSelectionChange: (
    updater:
      | Record<string, boolean>
      | ((old: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  /** Shows a "Grant access" button in the section header when given. */
  onGrant?: () => void;
}) {
  const columns: Columns<AccessRow> = useMemo(
    () => [
      {
        id: "name",
        accessorFn: (row: AccessRow) => subjectDisplay(row).label,
        Cell: ({ row }: { row: { original: AccessRow } }) => (
          <DisplayNameCell
            row={row.original}
            fallbackLabel={subjectDisplay(row.original).label}
          />
        ),
        header: "Name",
        meta: { primaryColumn: true },
      },
      {
        id: "email",
        accessorFn: (row: AccessRow) => row.info?.email ?? row.info?.username,
        Cell: ({ row }: { row: { original: AccessRow } }) => (
          <EmailCell row={row.original} />
        ),
        header: "Email",
        enableGlobalFilter: !flush,
      },
      {
        id: "accessType",
        filterVariant: "select",
        accessorFn: (row: AccessRow) =>
          row.access === "direct" ? "Granted" : "Inherited",
        Cell: ({ row }: { row: { original: AccessRow } }) =>
          row.original.access === "direct" ? (
            <em className="bfc-base-2">Granted</em>
          ) : (
            // `via` names the group or parent object the access cascades from.
            <Tooltip content={row.original.via ?? "Inherited"}>
              <em className="bfc-base-2">Inherited</em>
            </Tooltip>
          ),
        header: "Access type",
        size: 140,
        enableGlobalFilter: !flush,
      },
      {
        id: "role",
        filterVariant: "multi-select",
        accessorFn: (row: AccessRow) => ROLE_LABEL[row.relation],
        Cell: ({ row }: { row: { original: AccessRow } }) => (
          <RoleBadge role={row.original.relation} />
        ),
        header: "Role",
        size: 160,
        enableGlobalFilter: !flush,
      },
    ],
    // Embedded (flush): the table's search looks at the Name column only.
    [flush],
  );

  // Pinned order: the signed-in user first whatever their role, then owners,
  // then everyone else, each group alphabetical. Otherwise the table sorts by
  // name on its own (below).
  const token = auth.keycloak.tokenParsed;
  const mySub = token?.sub?.toLowerCase();
  const myEmail = (token?.email as string | undefined)?.toLowerCase();
  const orderedRows = useMemo(() => {
    if (!pinYouAndOwners) return rows;
    // The row's subject is `user:<id>`; the id and email Keycloak attached to
    // the row are fallbacks, and everything is compared case-insensitively.
    const isMe = (row: AccessRow) =>
      row.subjectType === "user" &&
      Boolean(
        (mySub &&
          (row.subject.toLowerCase() === `user:${mySub}` ||
            row.info?.id?.toLowerCase() === mySub)) ||
          (myEmail && row.info?.email?.toLowerCase() === myEmail),
      );
    const group = (row: AccessRow) =>
      isMe(row) ? 0 : row.relation === "owner" ? 1 : 2;
    return [...rows].sort(
      (a, b) =>
        group(a) - group(b) ||
        subjectDisplay(a).label.localeCompare(subjectDisplay(b).label),
    );
  }, [rows, pinYouAndOwners, mySub, myEmail]);

  // The sort is controlled here, not left to the table's own state: pinned
  // starts unsorted so the rows show in the order above, the others start
  // sorted by name. Clicking a column header still sorts.
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>(
    pinYouAndOwners ? [] : [{ id: "name", desc: false }],
  );

  return (
    <Section border={!flush} radius={!flush} background="base-2">
      {/* A flush (embedded) table sits under its container's own heading. */}
      {!flush && (
        <Section.Header style={{ backgroundColor: "var(--bfc-base-3)" }}>
          <Inline align="center">
            <Icon icon={faUserCheck} />
            <Inline.Stretch>Existing access</Inline.Stretch>
            {onGrant && showGrantButton && (
              <Button variant="flat" small onClick={onGrant}>
                <Icon icon={faUserGear} marginRight />
                Manage access
              </Button>
            )}
          </Inline>
        </Section.Header>
      )}
      {/* The table's own search stays where it is; this only labels it. The
          toolbar has 16px of top padding and the same background as the
          section, so the label is pulled 12px down into it (above the toolbar
          in stacking order) to sit 4px over the input. */}
      {flush && (
        <div
          style={{
            padding: "0 16px",
            marginBottom: -12,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Label>Search for name</Label>
        </div>
      )}
      <DataTable
        border={false}
        columns={columns}
        data={orderedRows}
        name="management-access"
        tableOptions={{
          enableColumnOrdering: false,
          enablePagination: false,
          enableRowSelection: Boolean(onGrant),
          // Index ids would shift when the data refetches, moving the selection to other people.
          getRowId: accessRowKey,
          globalFilterFn: "contains",
          onRowSelectionChange,
          onSortingChange: setSorting,
          state: { rowSelection, sorting },
          // The table mirrors sort, search, filters and page into the URL and
          // reads them back on load, which is how a leftover `?sort=name` kept
          // overriding the pinned order. A table embedded in a client object
          // must not touch the page's URL, so it opts out.
          ...(pinYouAndOwners && {
            queryKeys: {
              filter: false,
              page: false,
              search: false,
              sort: false,
            },
          }),
        }}
        emptyState={{ title: "No access." }}
      />
    </Section>
  );
}

/** Lets a parent open the manage-access drawer from its own button. */
export type ManagementAccessHandle = { openManage: () => void };

export function ManagementAccessSection({
  objectId,
  objectLabel,
  heading,
  grantButton = "page",
  embedded = false,
  manageRef,
}: {
  /** e.g. the Platform's management object id, `client:<id>`, `blueprint:<slug>`. */
  objectId: string;
  /** Human noun for copy, e.g. "platform", "client", "blueprint". */
  objectLabel: string;
  /** Replaces the default "Management access" title and byline. */
  heading?: ReactNode;
  /**
   * Where the "Grant access" button sits: the page header row, or the table
   * section header. "external" draws none; the parent opens the drawer through
   * `manageRef` (checked rows still become its starting subjects).
   */
  grantButton?: "page" | "table" | "external";
  manageRef?: Ref<ManagementAccessHandle>;
  /**
   * For use inside a container that has no padding of its own (an accordion
   * item): only the access table, flush to the edges (no border or radius), with
   * no heading and no people box.
   */
  embedded?: boolean;
}) {
  const queryClient = useQueryClient();
  const queryKey = ["management", "access", objectId];
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getManagementAccess(objectId),
  });

  const [notice, setNotice] = useState<{
    state: "warning" | "alert";
    text: string;
  } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [manageSubjects, setManageSubjects] = useState<ManageSubject[]>([]);

  const [manageSession, setManageSession] = useState(0);

  // The section returns early while loading, so the function that knows the
  // rows is only built further down; the handle reaches it through this ref.
  const grantSelectedRef = useRef<() => void>(() => {});
  useImperativeHandle(
    manageRef,
    () => ({ openManage: () => grantSelectedRef.current() }),
    [],
  );

  function openManage(subjects: ManageSubject[]) {
    setManageSubjects(subjects);
    setManageSession((n) => n + 1);
    setAddOpen(true);
  }

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey });
  }

  // Rendered in every state, so the heading doesn't pop in (and shift the page) once loaded.
  const headerBlock = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
      }}
    >
      {heading ?? (
        <Grid gap={4}>
          <span className="bf-h4">
            <Icon
              icon={faUserShield}
              style={{ marginRight: 8, color: "var(--bfc-theme)" }}
            />
            Management access
          </span>
          <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
            Who can manage this {objectLabel}, and who can grant others access.
          </p>
        </Grid>
      )}
      {grantButton === "page" && (
        <Button
          state={isLoading || error ? "inactive" : undefined}
          onClick={isLoading || error ? undefined : () => openManage([])}
        >
          <Icon icon={faUserPlus} marginRight />
          Grant access
        </Button>
      )}
    </div>
  );

  const header = embedded ? null : headerBlock;
  // Embedded has no padding from its container, so states that are not the
  // table carry their own.
  const pad = embedded ? { padding: 16 } : undefined;

  if (isLoading) {
    return (
      <Grid gap={24} style={pad}>
        {header}
        <Grid gap={16} style={{ minHeight: 100, placeContent: "center" }}>
          <Icon.Spinner size={24} />
        </Grid>
      </Grid>
    );
  }

  if (error) {
    if (
      error instanceof IntilityIdException &&
      error.code === FgaErrorCodes.Forbidden
    ) {
      return (
        <Grid gap={24} style={pad}>
          {header}
          <Message state="neutral">
            Only an owner of this {objectLabel} can view and manage its access.
          </Message>
        </Grid>
      );
    }
    return (
      <Grid gap={24} style={pad}>
        {header}
        <Message state="warning" header="Failed to load access">
          {error instanceof Error ? error.message : "Unknown error"}
        </Message>
      </Grid>
    );
  }

  // keycloak-api holds owner on module:keycloak/module:spiffe so it can provision platforms and
  // clients on the platform's behalf — real access, but noise on every object's list. Hide it
  // here; the tuple itself is untouched.
  function strongestPerSubject(kind: "direct" | "indirect"): AccessRow[] {
    const strongest = new Map<string, AccessRow>();
    if (data) {
      for (const relation of ROLE_ORDER) {
        for (const principal of data[relation]) {
          if ((principal.access === "direct") !== (kind === "direct")) continue;
          if (!strongest.has(principal.subject)) {
            strongest.set(principal.subject, { ...principal, relation });
          }
        }
      }
    }
    return [...strongest.values()].filter(
      (row) => row.subject !== "client:keycloak-api",
    );
  }
  const directRows = strongestPerSubject("direct");
  const directRankBySubject = new Map<string, number>(
    directRows.map((row) => [row.subject, ROLE_RANK[row.relation]]),
  );
  // A subject's own direct role implies every weaker relation (owner ⊇ maintainer ⊇
  // viewer), which surfaces here as spurious "indirect" entries at or below that rank
  // (ADR-0038 lets a subject hold independent roles on parent and child, so ancestor
  // access at the same-or-weaker relation is not new information). Only a strictly
  // stronger relation inherited from an ancestor is worth showing.
  const indirectRows = strongestPerSubject("indirect").filter((row) => {
    const directRank = directRankBySubject.get(row.subject);
    return directRank === undefined || ROLE_RANK[row.relation] > directRank;
  });
  const rows: AccessRow[] = [...directRows, ...indirectRows];

  function grantSelected() {
    // Checked rows become the drawer's starting subjects (once per subject).
    const picked = new Map<string, ManageSubject>();
    for (const row of rows) {
      if (!rowSelection[accessRowKey(row)]) continue;
      if (picked.has(row.subject)) continue;
      picked.set(row.subject, {
        subject: row.subject,
        label: subjectDisplay(row).label,
        email: null,
        principal: row,
      });
    }
    openManage([...picked.values()]);
  }
  grantSelectedRef.current = grantSelected;

  return (
    <Grid gap={embedded ? 0 : 24}>
      {header}

      {notice && (
        <div style={pad}>
          <Message state={notice.state} onClose={() => setNotice(null)}>
            {notice.text}
          </Message>
        </div>
      )}

      {rows.length === 0 ? (
        <div style={pad}>
          <Message state="neutral">
            No access grants found for this {objectLabel}.
          </Message>
        </div>
      ) : (
        <div className="access-columns">
          {directRows.length > 0 && !embedded && (
            <div className="access-people">
              <PeopleSection rows={directRows} />
            </div>
          )}
          <div className="access-table">
            <DirectAccessTable
              flush={embedded}
              pinYouAndOwners={embedded}
              rows={rows}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              onGrant={grantButton === "page" ? undefined : grantSelected}
              showGrantButton={grantButton === "table"}
            />
          </div>
        </div>
      )}

      <AddAccessDrawer
        isOpen={addOpen}
        sessionKey={manageSession}
        objectId={objectId}
        objectLabel={objectLabel}
        initialSubjects={manageSubjects}
        directRoles={new Map(directRows.map((r) => [r.subject, r.relation]))}
        inheritedRoles={
          new Map(indirectRows.map((r) => [r.subject, r.relation]))
        }
        onClose={() => setAddOpen(false)}
        onChanged={() => {
          setRowSelection({});
          invalidate();
        }}
        onApplied={(result) => {
          setAddOpen(false);
          const notes: string[] = [];
          if (result.superseded.length > 0) {
            notes.push(`Also removed: ${supersededText(result.superseded)}.`);
          }
          if (result.skipped.length > 0) {
            notes.push(
              `Already had an equal or stronger inherited role: ${result.skipped.join(", ")}.`,
            );
          }
          if (result.noDirectRole.length > 0) {
            notes.push(
              `No granted role to remove for: ${result.noDirectRole.join(", ")}.`,
            );
          }
          if (notes.length > 0) {
            setNotice({ state: "warning", text: notes.join(" ") });
          }
        }}
      />
    </Grid>
  );
}

const SUBJECT_TYPE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "client", label: "Client" },
  { value: "group", label: "Group" },
];

const ROLE_OPTIONS = ROLE_ORDER.map((r) => ({
  value: r,
  label: ROLE_LABEL[r],
}));

// The drawer's role control: a management role, or removing the granted role altogether.
interface RoleChoice {
  value: ManagementRole | "remove";
  label: string;
}
const ROLE_CHOICES: RoleChoice[] = [
  ...ROLE_OPTIONS,
  { value: "remove", label: "Remove access" },
];

interface ApplyResult {
  superseded: ManagementSupersededGrant[];
  /** Labels of subjects left alone because they already hold an equal or stronger inherited role. */
  skipped: string[];
  /** Labels of subjects with no granted role, so "Remove access" had nothing to remove. */
  noDirectRole: string[];
}

function AddAccessDrawer({
  isOpen,
  sessionKey,
  objectId,
  objectLabel,
  initialSubjects,
  directRoles,
  inheritedRoles,
  onClose,
  onChanged,
  onApplied,
}: {
  isOpen: boolean;
  /** Changes on every open, to reset the form. */
  sessionKey: number;
  objectId: string;
  objectLabel: string;
  initialSubjects: ManageSubject[];
  /** Each subject's current direct role on this object, if any. */
  directRoles: Map<string, ManagementRole>;
  /** Each subject's inherited role on this object, if it is stronger than their direct one. */
  inheritedRoles: Map<string, ManagementRole>;
  onClose: () => void;
  /** Something was written; the access list needs a refetch. */
  onChanged: () => void;
  /** Every subject was handled without error. */
  onApplied: (result: ApplyResult) => void;
}) {
  // The form owns the Drawer (its footer needs the form's state). `sessionKey` remounts it on
  // every open, so each open starts from the subjects handed in.
  return (
    <AddAccessForm
      key={sessionKey}
      isOpen={isOpen}
      objectId={objectId}
      objectLabel={objectLabel}
      initialSubjects={initialSubjects}
      directRoles={directRoles}
      inheritedRoles={inheritedRoles}
      onClose={onClose}
      onChanged={onChanged}
      onApplied={onApplied}
    />
  );
}

const MANAGE_FORM_ID = "manage-access-form";

function AddAccessForm({
  isOpen,
  objectId,
  objectLabel,
  initialSubjects,
  directRoles,
  inheritedRoles,
  onClose,
  onChanged,
  onApplied,
}: {
  isOpen: boolean;
  objectId: string;
  objectLabel: string;
  initialSubjects: ManageSubject[];
  directRoles: Map<string, ManagementRole>;
  inheritedRoles: Map<string, ManagementRole>;
  onClose: () => void;
  onChanged: () => void;
  onApplied: (result: ApplyResult) => void;
}) {
  const [subjects, setSubjects] = useState<ManageSubject[]>(initialSubjects);
  const [subjectType, setSubjectType] = useState<
    (typeof SUBJECT_TYPE_OPTIONS)[number]
  >(SUBJECT_TYPE_OPTIONS[0]);
  // Users are picked by name/email search (resolved to a GUID); clients and
  // groups have no such lookup here, so those stay a typed identifier.
  const [userQuery, setUserQuery] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [role, setRole] = useState<RoleChoice>(
    ROLE_OPTIONS[ROLE_OPTIONS.length - 1],
  );
  const isRemove = role.value === "remove";
  const [pending, setPending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const { data: userResults = [] } = useSearchUsers(userQuery);

  function changeSubjectType(next: (typeof SUBJECT_TYPE_OPTIONS)[number]) {
    setSubjectType(next);
    setUserQuery("");
    setSubjectId("");
  }

  function addSubject(next: ManageSubject) {
    setSubjects((current) =>
      // Newest first, so a fresh addition is visible without scrolling.
      current.some((s) => s.subject === next.subject)
        ? current
        : [next, ...current],
    );
  }

  function removeSubject(subject: string) {
    setSubjects((current) => current.filter((s) => s.subject !== subject));
  }

  const isUser = subjectType.value === "user";
  // For users, Add takes the top search match; a click on any result adds that one.
  const topMatch: UserSearchResult | undefined =
    userQuery.length >= 2 ? userResults[0] : undefined;
  const canAddTyped = isUser ? topMatch !== undefined : subjectId.trim() !== "";
  const canSubmit = subjects.length > 0 && !pending;

  function addUser(user: UserSearchResult) {
    addSubject({
      subject: `user:${user.userId}`,
      label: user.displayName,
      email: user.email,
    });
    setUserQuery("");
  }

  function addTyped() {
    if (!canAddTyped) return;
    if (isUser) {
      if (topMatch) addUser(topMatch);
      return;
    }
    const id = subjectId.trim();
    addSubject({
      subject: `${subjectType.value}:${id}`,
      label: id,
      email: null,
    });
    setSubjectId("");
  }

  // Subjects whose granted role this change removes or lowers. They need a confirm first.
  const losing = subjects.filter((s) => {
    const direct = directRoles.get(s.subject);
    if (direct === undefined) return false;
    return (
      isRemove || ROLE_RANK[role.value as ManagementRole] < ROLE_RANK[direct]
    );
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    if (losing.length > 0) {
      setConfirmOpen(true);
      return;
    }
    apply();
  }

  // Applies the chosen role to every queued subject, one at a time. Raising goes through a
  // plain grant, lowering through the demote verb with the role we last saw as the guard
  // (ADR-0038), and a subject who already has the role is left alone.
  async function apply() {
    setConfirmOpen(false);
    setPending(true);
    setErrors([]);

    const target = role.value;
    const failures: string[] = [];
    const skipped: string[] = [];
    const noDirectRole: string[] = [];
    const superseded: ManagementSupersededGrant[] = [];
    let changed = false;

    for (const s of subjects) {
      const name = s.principal ? subjectDisplay(s.principal).label : s.label;
      const direct = directRoles.get(s.subject);
      const inherited = inheritedRoles.get(s.subject);
      try {
        if (target === "remove") {
          // Only a granted role can be removed here; inherited access is removed where it is
          // granted.
          if (direct === undefined) {
            noDirectRole.push(name);
            continue;
          }
          await deleteManagementTuple({
            user: s.subject,
            relation: direct,
            object: objectId,
          });
          changed = true;
          continue;
        }
        if (direct === target) continue;
        if (direct === undefined) {
          if (inherited && ROLE_RANK[target] <= ROLE_RANK[inherited]) {
            skipped.push(name);
            continue;
          }
          const result = await writeManagementTuple({
            user: s.subject,
            relation: target,
            object: objectId,
          });
          superseded.push(...result.superseded);
        } else if (ROLE_RANK[target] > ROLE_RANK[direct]) {
          const result = await writeManagementTuple({
            user: s.subject,
            relation: target,
            object: objectId,
          });
          superseded.push(...result.superseded);
        } else {
          const result = await demoteManagementRole({
            user: s.subject,
            relation: target,
            from: direct,
            object: objectId,
          });
          superseded.push(...result.superseded);
        }
        changed = true;
      } catch (err) {
        const stale =
          err instanceof IntilityIdException &&
          err.code === FgaErrorCodes.RoleAssignmentMismatch;
        // The demote guard saw a role we no longer hold on screen: refetch, so a retry
        // starts from the current role.
        if (stale) changed = true;
        const reason = stale
          ? "Someone else changed this access. The current role is reloaded, try again."
          : err instanceof Error
            ? err.message
            : "Failed to update access.";
        failures.push(`${name}: ${reason}`);
      }
    }

    if (changed) onChanged();
    setPending(false);
    if (failures.length > 0) {
      setErrors(failures);
      return;
    }
    onApplied({ superseded, skipped, noDirectRole });
  }

  return (
    <Drawer
      isOpen={isOpen}
      onRequestClose={onClose}
      // Fixed footer: stays at the bottom while the form above scrolls.
      footer={
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 8 }}
        >
          <Button
            type="button"
            variant="flat"
            onClick={pending ? undefined : onClose}
            state={pending ? "inactive" : undefined}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={MANAGE_FORM_ID}
            variant="filled"
            state={canSubmit ? (isRemove ? "alert" : undefined) : "inactive"}
          >
            {pending
              ? "Applying…"
              : isRemove
                ? subjects.length > 1
                  ? `Remove access for ${subjects.length} subjects`
                  : "Remove access"
                : subjects.length > 1
                  ? `Apply to ${subjects.length} subjects`
                  : "Apply role"}
          </Button>
        </div>
      }
    >
      {/* The header is ours, not the Drawer's `header` prop: Drawer renders that inside the
          same scrolling area as the children, so a full-height form would overflow it. Here
          header + form fill the area exactly and only the list scrolls. */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <header className="bf-drawer-header">Manage access</header>
        <form
          id={MANAGE_FORM_ID}
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 1,
            minHeight: 0,
          }}
        >
          <p className="bfc-base-2" style={{ margin: 0 }}>
            Set a management role on this {objectLabel} for everyone listed
            below. Add more users, clients, or groups to include them in the
            same change.
          </p>

          <FieldGroup
            label="Add a user, client, or group"
            style={{ minWidth: "100%" }}
          >
            <Select
              label="Add a user, client, or group"
              hideLabel
              style={{ maxWidth: "120px" }}
              options={SUBJECT_TYPE_OPTIONS}
              value={subjectType}
              onChange={(opt) =>
                opt &&
                changeSubjectType(opt as (typeof SUBJECT_TYPE_OPTIONS)[number])
              }
            />

            {isUser ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "start",
                  gap: 8,
                  flex: "1",
                }}
              >
                <div style={{ flex: 1, position: "relative" }}>
                  <Input
                    label="Search users"
                    hideLabel
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTyped();
                      }
                    }}
                    placeholder="Name or email address"
                  />
                  {userQuery.length >= 2 && userResults.length > 0 && (
                    <ul
                      // An overlay under the field: it floats over the content below instead of
                      // pushing it down.
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        marginTop: 4,
                        listStyle: "none",
                        padding: 0,
                        backgroundColor: "var(--bfc-base-2)",
                        border: "1px solid var(--bfc-base-c-dimmed)",
                        borderRadius: 4,
                        maxHeight: 280,
                        overflowY: "auto",
                      }}
                    >
                      {userResults.map((r: UserSearchResult) => (
                        <li key={r.userId}>
                          <button
                            type="button"
                            onClick={() => addUser(r)}
                            style={{
                              display: "block",
                              width: "100%",
                              padding: "8px 12px",
                              border: 0,
                              background: "none",
                              textAlign: "left",
                              cursor: "pointer",
                            }}
                          >
                            <span style={{ display: "block" }}>
                              {r.displayName}
                            </span>
                            {r.email && (
                              <span
                                className="bf-small bfc-base-2"
                                style={{ display: "block" }}
                              >
                                {r.email}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={addTyped}
                  state={canAddTyped ? undefined : "inactive"}
                >
                  Add
                </Button>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "end",
                  gap: 8,
                  flex: "1",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Input
                    hideLabel
                    label={
                      subjectType.value === "group"
                        ? "Group name"
                        : "Client ID (GUID)"
                    }
                    value={subjectId}
                    onChange={(e) => setSubjectId(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTyped();
                      }
                    }}
                    placeholder={
                      subjectType.value === "group"
                        ? "e.g. equinor-developers"
                        : "Keycloak object GUID"
                    }
                  />
                </div>
                <Button
                  type="button"
                  onClick={addTyped}
                  state={canAddTyped ? undefined : "inactive"}
                >
                  Add
                </Button>
              </div>
            )}
          </FieldGroup>

          {/* Not a FieldGroup: that is a flex row built for inputs. Equal-width grid columns
              widen the buttons without touching their height. */}
          <fieldset style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
            <legend className="bf-label" style={{ padding: 0 }}>
              Role
            </legend>
            <Button.Group
              style={{
                display: "grid",
                gridAutoFlow: "column",
                gridAutoColumns: "minmax(max-content, 1fr)",
                width: "100%",
              }}
            >
              {ROLE_CHOICES.map((option) => {
                const isRemoveOption = option.value === "remove";
                const selected = role.value === option.value;
                return (
                  <Button
                    key={option.value}
                    type="button"
                    // Button.Group paints the selected button from the theme variables, so
                    // "Remove access" swaps them for the alert (red) ones: red text at rest,
                    // red fill when selected.
                    style={
                      {
                        whiteSpace: "nowrap",
                        ...(isRemoveOption && {
                          "--bfc-theme": "var(--bfc-alert)",
                          "--bfc-theme-2": "var(--bfc-alert-2)",
                          "--bfc-theme-hc": "var(--bfc-alert-c)",
                          ...(!selected && {
                            color: "var(--bfc-base-c-alert)",
                          }),
                        }),
                      } as React.CSSProperties
                    }
                    state={isRemoveOption && selected ? "alert" : undefined}
                    active={selected}
                    aria-pressed={selected}
                    onClick={() => setRole(option)}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Button.Group>
          </fieldset>
          {errors.length > 0 && (
            <Message state="alert" header="Some changes failed">
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </Message>
          )}
          {/* Label + box fill the space down to the footer; the box only scrolls once the
              rows no longer fit. */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <div
              id="affected-subjects-label"
              className="bf-label"
              style={{ marginBottom: 8 }}
            >
              Affected users, clients and groups
            </div>
            <ul
              aria-labelledby="affected-subjects-label"
              style={{
                backgroundColor: "var(--bfc-base-2)",
                listStyle: "none",
                margin: 0,
                // 12px around the rows and 12px between them.
                padding: 12,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                border: "1px solid var(--bfc-base-c-dimmed)",
                borderRadius: 4,
                flex: 1,
                minHeight: 120,
                overflowY: "auto",
              }}
            >
              {subjects.length === 0 && (
                <li className="bfc-base-2">No one added yet.</li>
              )}
              {subjects.map((s) => (
                <li
                  key={s.subject}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <CurrentRoleBadge
                    role={
                      inheritedRoles.get(s.subject) ??
                      directRoles.get(s.subject)
                    }
                  />
                  <span style={{ flex: 1 }}>
                    {s.principal ? (
                      <>
                        <DisplayNameCell
                          row={s.principal}
                          fallbackLabel={s.label}
                        />
                        <span
                          className="bf-small bfc-base-2"
                          style={{ display: "block" }}
                        >
                          <EmailCell row={s.principal} />
                        </span>
                      </>
                    ) : (
                      <>
                        {s.label}
                        {s.email && (
                          <span
                            className="bf-small bfc-base-2"
                            style={{ display: "block" }}
                          >
                            {s.email}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                  <Button
                    type="button"
                    variant="flat"
                    small
                    onClick={() => removeSubject(s.subject)}
                    aria-label={`Remove ${s.label} from this change`}
                  >
                    <Icon icon={faTimes} />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </form>
      </div>
      <Modal
        isOpen={confirmOpen}
        onRequestClose={() => setConfirmOpen(false)}
        header={isRemove ? "Remove access" : "Lower role"}
        width={480}
      >
        <Grid gap={16}>
          <p className="bf-p" style={{ margin: 0 }}>
            {isRemove
              ? `Remove the granted role on this ${objectLabel} for:`
              : `Lower the granted role to ${role.label} on this ${objectLabel} for:`}
          </p>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {losing.map((s) => (
              <li key={s.subject}>
                <strong>
                  {s.principal ? subjectDisplay(s.principal).label : s.label}
                </strong>{" "}
                ({ROLE_LABEL[directRoles.get(s.subject) as ManagementRole]})
              </li>
            ))}
          </ul>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="flat" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="filled" state="alert" onClick={apply}>
              {isRemove ? "Remove access" : "Lower role"}
            </Button>
          </div>
        </Grid>
      </Modal>
    </Drawer>
  );
}
